<script setup lang="ts">
import { useBuildUrl } from '@/composables/utils/useBuildUrl';
import { OptimizedImage } from '@/wabunka-src/common/components';
import { useOptimizedImageUrl } from '@/wabunka-src/common/composables/general/useOptimizedImageUrl';

definePageMeta({ lp: { indexable: true } });

const { buildUrlWithEndSlash } = useBuildUrl();
const getOptimizedImageUrl = useOptimizedImageUrl();

useHead({
  link: [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,200,0,-25&display=swap',
    },
  ],
});

useSeoMeta({
  title: 'Tailored Travel Booking Process | Wabunka',
  description: 'A step-by-step overview of how to book a tailored travel experience with Wabunka — from initial inquiry to your final travel documents.',
  ogTitle: 'Tailored Travel Booking Process',
  ogDescription: 'A step-by-step overview of how to book a tailored travel experience with Wabunka.',
  ogUrl: buildUrlWithEndSlash('/lp/booking-process'),
  twitterCard: 'summary_large_image',
  twitterTitle: 'Tailored Travel Booking Process',
  twitterDescription: 'A step-by-step overview of how to book a tailored travel experience with Wabunka.',
});

// TODO: 画像は microCMS アップロード後に配信 URL へ差し替える
const heroImage = {
  url: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/91cee7ac133141ec98f8023a6c232f42/Wabunka_travel_image.png',
  alt: 'Wabunka tailored travel',
};
const bandImage = {
  url: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/15fb7024135f4fcda09a97fd1429b2b8/wabunka_experiences_image.png',
  alt: 'Wabunka experience',
};
const closingImage = {
  url: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/cf398806300b407d95424f026d6666fb/sightseen_image.png',
  alt: 'Wabunka closing visual',
};
// CSS の background-image は OptimizedImage を通せないため、URL だけ最適化する
const closingBackgroundImage = `url(${getOptimizedImageUrl(closingImage.url, { width: 1920, quality: 70 })})`;

interface StepItem {
  number: string;
  title: string;
  team: string;
  guest: string;
}

const steps: StepItem[] = [
  {
    number: '01',
    title: 'Initial Inquiry',
    team: 'Receive your travel request through <a href="https://docs.google.com/forms/d/e/1FAIpQLSdQ6FMqXupDTDK_hCUIR8iSkMxwbJKQshV8a-5K05SjPyja1Q/viewform" rel="noopener" target="_blank">our online inquiry form</a> or contact page. We note your preferred dates, destinations, interests, group size, and any special requests.',
    guest:
      'Submit a tailored travel inquiry with your desired travel dates, interests, budget, and other preferences. Provide as much detail as possible so we can craft an ideal experience. <a href="#urgent">*Note on Urgent Inquiries</a>',
  },
  {
    number: '02',
    title: 'Consultation &amp; Proposal',
    team: 'We prepare a preliminary itinerary or proposal showcasing a customized trip outline based on your requests.',
    guest: 'Review the itinerary/proposal we provide, and share your feedback or any adjustments you&rsquo;d like.',
  },
  {
    number: '03',
    title: 'Confirmation &amp; Deposit',
    team: 'Refine the itinerary based on your feedback and present a detailed trip plan along with a price quote. We then issue a booking agreement and an invoice for a deposit to secure your trip arrangements.',
    guest:
      'Confirm you&rsquo;re happy with the proposed itinerary and price. Secure your booking by paying the required deposit (percentage of the trip cost) by the due date indicated. This deposit allows us to begin firm reservations for your trip.',
  },
  {
    number: '04',
    title: 'Itinerary Refinement',
    team: 'With the deposit received, our team proceeds to make all necessary arrangements: reserving accommodations, booking guides, experiences, and transportation. We keep you updated as we lock in each element. If any adjustments are needed (due to availability, etc.), we collaborate with you on alternatives and finalize the itinerary details.',
    guest:
      'Continue to provide input as needed while we fine-tune your trip. Promptly review any updates or changes we send you (such as alternative activity options if an original choice is unavailable) and give your approval so we can finalize all bookings.',
  },
  {
    number: '05',
    title: 'Balance Payment',
    team: 'Once the itinerary is finalized, we send you a final invoice for the remaining balance of the trip cost. (Typically, the balance payment is due by around 30 days before your departure date, depending on your travel start date.)',
    guest:
      'Review the finalized itinerary and trip cost. Settle the remaining balance by the specified deadline to fully confirm all services. We accept major credit cards for convenient payment of the balance.',
  },
  {
    number: '06',
    title: 'Final Travel Documents',
    team: 'After receiving full payment, we secure any last confirmations and prepare your travel packet. We deliver your detailed final itinerary, vouchers/tickets for experiences and hotels, and any other necessary travel documents. Our team provides all the information you’ll need, and we remain available for any questions.',
    guest:
      'Receive your final travel documents (itinerary, vouchers, contact numbers, etc.). Look over all details and ask us any last-minute questions. Once everything is in order, prepare for your journey! Rest assured, our support team is on standby throughout your trip should you need assistance.',
  },
];

interface PaymentCard {
  icon: string;
  title: string;
  body: string;
}

const paymentCards: PaymentCard[] = [
  {
    icon: 'savings',
    title: 'Deposit',
    body: 'A deposit is required to confirm your tailored trip — typically <strong>around 30%</strong> of the total trip cost. The exact amount and due date are communicated after you approve the initial proposal, with the due date about <strong>2 months before</strong> your trip start date. It allows us to commence firm bookings on your behalf. <em>This deposit is non-refundable if you cancel (see Cancellation Policy), as it covers preliminary arrangements and commitments.</em>',
  },
  {
    icon: 'receipt_long',
    title: 'Balance Payment',
    body: 'The remaining balance must be paid in full by the specified deadline, usually about <strong>30 days before your trip start date</strong> (the exact deadline will be in your invoice/booking agreement). Timely payment ensures all bookings — hotels, transport, experiences — are secured and ticketed in your name.',
  },
  {
    icon: 'credit_card',
    title: 'Accepted Methods',
    body: 'We currently accept major credit cards (Visa, MasterCard, American Express, etc.) for all payments. All prices will be quoted and charged in <strong>Japanese Yen (JPY)</strong>.',
  },
];

interface InfoCard {
  icon: string;
  title: string;
  body: string;
  link?: { label: string; href: string };
}

const infoCards: InfoCard[] = [
  {
    icon: 'event_available',
    title: 'Advance Booking',
    body: 'To ensure we can secure the most exclusive experiences and accommodations for you, we recommend starting the booking process <strong>a few months in advance</strong> of your travel dates. This is especially important for peak travel seasons or highly sought-after activities. Early planning increases the likelihood of availability and gives us ample time to tailor every detail to your liking.',
    link: {
      label: 'VIP Travel Booking & Recommended Timeline Guidelines',
      href: '/lp/vip-timeline/',
    },
  },
  {
    icon: 'edit_calendar',
    title: 'Itinerary Changes',
    body: 'If you need to make changes or add-ons, contact us as soon as possible. We will do our best to adjust your plans (additional charges or change fees may apply depending on the request and vendor policies).',
  },
  {
    icon: 'interests',
    title: 'Personalization',
    body: 'We encourage you to communicate any particular interests (e.g., dietary requirements, special occasions, must-see activities) during the planning process. Wabunka is committed to crafting an itinerary that exceeds your expectations and provides truly unique experiences.',
  },
];
</script>

<template>
  <div class="lp-booking-process">
    <section class="hero">
      <div class="hero__grid">
        <div class="hero__text">
          <div class="hero__eyebrow">The Booking Process</div>
          <h1 class="hero__title">Tailored Travel Booking Process</h1>
          <p class="hero__lead">
            Experience Japan your way with Wabunka&rsquo;s personalized travel planning. Our booking process is designed to be straightforward and transparent, ensuring you know what to expect at each
            stage of creating your bespoke journey. Below is a step-by-step overview of how to book a tailored travel experience with Wabunka.
          </p>
        </div>
        <div class="hero__imgwrap">
          <OptimizedImage class="hero__img" fetchpriority="high" :alt="heroImage.alt" :image-url="heroImage.url" preload />
        </div>
      </div>
    </section>

    <section class="steps">
      <div class="steps__head">
        <h2 class="steps__title">Six Steps to Your Journey</h2>
      </div>
      <div class="steps__list">
        <article v-for="step in steps" :key="step.number" class="step-card">
          <div class="step-card__head">
            <span class="step-card__num">{{ step.number }}</span>
            <h3 class="step-card__title" v-html="step.title" />
          </div>
          <div class="step-card__body">
            <div class="step-card__col">
              <div class="step-card__role">
                <span aria-hidden="true" class="icon">room_service</span>
                <span>Wabunka team</span>
              </div>
              <p class="step-card__text" v-html="step.team" />
            </div>
            <div class="step-card__col step-card__col--guest">
              <div class="step-card__role">
                <span aria-hidden="true" class="icon">person</span>
                <span>Guest (you)</span>
              </div>
              <p class="step-card__text" v-html="step.guest" />
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="urgent">
      <div id="urgent" class="urgent__inner">
        <span aria-hidden="true" class="icon urgent__icon">schedule</span>
        <p class="urgent__text">
          <strong>Note on Urgent Inquiries.</strong> Please note that inquiries received within 60 days of the service date are subject to a 20% Last-minute Handling Fee due to the increased
          operational burden of expedited coordination.
        </p>
      </div>
    </section>

    <section class="payment">
      <div class="payment__inner">
        <div class="section-eyebrow">
          <span aria-hidden="true" class="icon">account_balance_wallet</span>
          <span>Payment Conditions</span>
        </div>
        <h2 class="section-title">How payments are structured</h2>
        <div class="payment__grid">
          <div v-for="card in paymentCards" :key="card.title" class="payment-card">
            <h3 class="payment-card__title">
              <span aria-hidden="true" class="icon">{{ card.icon }}</span>
              {{ card.title }}
            </h3>
            <p class="payment-card__body" v-html="card.body" />
          </div>
        </div>
      </div>
    </section>

    <section class="band">
      <div class="band__imgwrap band__imgwrap--full">
        <OptimizedImage class="band__img" loading="lazy" :alt="bandImage.alt" :image-url="bandImage.url" />
      </div>
      <div class="band__imgwrap band__imgwrap--half band__imgwrap--top">
        <OptimizedImage class="band__img band__img--left" loading="lazy" :alt="bandImage.alt" :image-url="bandImage.url" />
      </div>
      <div class="band__imgwrap band__imgwrap--half band__imgwrap--bottom">
        <OptimizedImage class="band__img band__img--right" loading="lazy" :alt="bandImage.alt" :image-url="bandImage.url" />
      </div>
    </section>

    <section class="cancel-contact">
      <div class="cancel-contact__col cancel-contact__col--cancel">
        <div class="section-eyebrow">
          <span aria-hidden="true" class="icon">policy</span>
          <span>Cancellation Policy</span>
        </div>
        <h2 class="section-title section-title--sm">If plans change</h2>
        <p class="cancel-contact__lead">We understand that travel plans can change. Wabunka adheres to a clear cancellation policy for tailored trips, outlined in your booking agreement.</p>
        <a class="cancel-contact__link" href="https://jcat.notion.site/GTA-Cancellation-policy-Shared-with-AGTs-and-guests-142d53e134b780078dbae3d764a35101" rel="noopener" target="_blank">
          <span class="cancel-contact__arrow">&rarr;</span>
          <span>GTA Cancellation Policy (Shared with AGTs and guests)</span>
        </a>
      </div>
      <div class="cancel-contact__col">
        <div class="section-eyebrow">
          <span aria-hidden="true" class="icon">support_agent</span>
          <span>Contact &amp; Support</span>
        </div>
        <h2 class="section-title section-title--sm">We&rsquo;re here to help</h2>
        <dl class="contact-list">
          <div class="contact-list__row">
            <dt class="contact-list__label">
              <span aria-hidden="true" class="icon">mail</span>
              <span>email</span>
            </dt>
            <dd class="contact-list__value">
              <a href="mailto:global-support@wabunka-media.jp">global-support@wabunka-media.jp</a> — feel free to email us anytime. We aim to respond within 2&ndash;3 business days.
            </dd>
          </div>
          <div class="contact-list__row">
            <dt class="contact-list__label">
              <span aria-hidden="true" class="icon">call</span>
              <span>phone</span>
            </dt>
            <dd class="contact-list__value">We will provide an emergency contact number once the booking is confirmed. (Our team is based in Japan, so please note the time zone difference.)</dd>
          </div>
          <div class="contact-list__row contact-list__row--last">
            <dt class="contact-list__label">
              <span aria-hidden="true" class="icon">flight_takeoff</span>
              <span>during your trip</span>
            </dt>
            <dd class="contact-list__value">Once your journey begins, we remain available for support.</dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="info">
      <div class="info__inner">
        <div class="section-eyebrow">
          <span aria-hidden="true" class="icon">info</span>
          <span>Additional Information</span>
        </div>
        <h2 class="section-title">Good to know before you begin</h2>
        <div class="info__grid">
          <div v-for="card in infoCards" :key="card.title" class="info-card">
            <div class="info-card__head">
              <span aria-hidden="true" class="icon info-card__icon">{{ card.icon }}</span>
              <h3 class="info-card__title">{{ card.title }}</h3>
            </div>
            <p class="info-card__body" v-html="card.body" />
            <NuxtLink v-if="card.link" class="info-card__link" :to="card.link.href">{{ card.link.label }}</NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="closing" :style="{ backgroundImage: closingBackgroundImage }">
      <div class="closing__overlay" />
      <div class="closing__inner">
        <p class="closing__lead">
          By following the above process, booking your tailored travel experience with Wabunka is smooth and worry-free.<br />
          We handle every detail from start to finish — all you need to do is share your travel dreams with us,<br />
          and we&rsquo;ll take care of the rest.
        </p>
        <div class="closing__rule" />
        <p class="closing__note">We look forward to creating an unforgettable Japanese adventure just for you.</p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.lp-booking-process {
  background: #f4f1ed;
  color: #221f1a;
  font-family: Spectral, serif;
  font-weight: 400;
  line-height: 1.7;
}

.lp-booking-process :deep(a) {
  color: #2b3a47;
  text-decoration: none;
  border-bottom: 1px solid rgb(43 58 71 / 28%);
}

.lp-booking-process :deep(a:hover) {
  color: #9a7b4f;
  border-bottom-color: rgb(154 123 79 / 60%);
}

.lp-booking-process :deep(.icon) {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  font-variation-settings:
    'FILL' 0,
    'wght' 200,
    'GRAD' 0,
    'opsz' 24;
  color: #9a7b4f;
  vertical-align: middle;
}

.container {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 40px;
}

/* Hero */
.hero {
  max-width: 1140px;
  margin: 0 auto;
  padding: 76px 40px 20px;
}

.hero__grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 56px;
  align-items: center;
}

.hero__eyebrow {
  font-size: 11px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: #9a7b4f;
  margin-bottom: 22px;
}

.hero__title {
  font-family: Spectral, serif;
  font-weight: 300;
  font-size: 50px;
  line-height: 1.06;
  letter-spacing: -0.01em;
  margin: 0 0 26px;
  color: #836324;
}

.hero__lead {
  font-size: 18px;
  line-height: 1.75;
  color: #4a4438;
  margin: 0;
}

.hero__imgwrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 4px;
  overflow: hidden;
  background: #e8e3da;
}

.hero__imgwrap :deep(.hero__img) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Steps */
.steps {
  max-width: 1140px;
  margin: 0 auto;
  padding: 82px 40px 30px;
}

.steps__head {
  border-bottom: 1px solid #e6dfd2;
  padding-bottom: 22px;
  margin-bottom: 30px;
}

.steps__title {
  font-family: Spectral, serif;
  font-weight: 300;
  font-size: 32px;
  line-height: 1.25;
  margin: 0;
  color: #836324;
}

.steps__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-card {
  background: #fff;
  border: 1px solid #ebe3d4;
  border-radius: 6px;
  padding: 30px 34px;
}

.step-card__head {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 22px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0eadd;
}

.step-card__num {
  font-family: Spectral, serif;
  font-size: 19px;
  color: #836324;
  letter-spacing: 0.04em;
}

.step-card__title {
  font-family: Spectral, serif;
  font-weight: 400;
  font-size: 23px;
  line-height: 1.25;
  margin: 0;
  color: #1d1a15;
}

.step-card__body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
}

.step-card__col--guest {
  padding-left: 40px;
  border-left: 1px solid #f0eadd;
}

.step-card__role {
  font-size: 18px;
  letter-spacing: 0.04em;
  color: #9a7b4f;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-card__role .icon {
  font-size: 16px;
}

.step-card__text {
  margin: 0;
  font-size: 16px;
  line-height: 1.68;
  color: #4a4438;
}

/* Urgent callout */
.urgent {
  max-width: 1140px;
  margin: 0 auto;
  padding: 8px 40px 0;
}

.urgent__inner {
  scroll-margin-top: 70px;
}

@media only screen and (min-width: 992px) {
  .urgent__inner {
    scroll-margin-top: 90px;
  }
}

.urgent__inner {
  padding: 12px 16px 0;
  display: flex;
  gap: 22px;
  align-items: flex-start;
}

.urgent__icon {
  font-size: 30px;
  padding-top: 2px;
}

.urgent__text {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 16px;
  color: #463f33;
}

/* Shared section heading */
.section-eyebrow {
  font-size: 11px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: #9a7b4f;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 9px;
}

.section-eyebrow .icon {
  font-size: 17px;
}

.section-title {
  font-family: Spectral, serif;
  font-weight: 300;
  font-size: 32px;
  line-height: 1.25;
  margin: 0 0 34px;
  color: #836324;
}

.section-title--sm {
  font-size: 30px;
  margin: 0 0 20px;
}

/* Payment conditions */
.payment {
  background: #f4f1ed;
}

.payment__inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 72px 40px;
}

.payment__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #e6dfd2;
  border: 1px solid #e6dfd2;
  border-radius: 4px;
  overflow: hidden;
}

.payment-card {
  background: #fff;
  padding: 30px 28px;
}

.payment-card__title {
  font-family: Spectral, serif;
  font-weight: 300;
  font-size: 21px;
  margin: 0 0 12px;
  color: #1d1a15;
  display: flex;
  align-items: center;
  gap: 11px;
}

.payment-card__body {
  margin: 0;
  font-size: 16px;
  color: #4a4438;
}

/* Cinematic band */
.band {
  background: #2b2723;
  font-size: 0;
  line-height: 0;
}

.band__imgwrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1407 / 342;
  background: #2b2723;
}

.band__imgwrap :deep(.band__img) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.band__imgwrap--half {
  display: none;
  aspect-ratio: 703.5 / 342;
}

.band__imgwrap :deep(.band__img--left) {
  object-position: left center;
}

.band__imgwrap :deep(.band__img--right) {
  object-position: right center;
}

/* Cancellation + Contact */
.cancel-contact {
  max-width: 1140px;
  margin: 0 auto;
  padding: 72px 40px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
}

.cancel-contact__col--cancel {
  padding-right: 64px;
  border-right: 1px solid #e2dacb;
}

.cancel-contact__lead {
  margin: 0 0 18px;
  font-size: 16.5px;
  color: #4a4438;
}

.cancel-contact__link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  border-bottom: none !important;
}

.cancel-contact__arrow {
  color: #836324;
  font-size: 18px;
  line-height: 1;
}

.cancel-contact__link span:last-child {
  text-decoration: underline;
  text-decoration-color: rgb(43 58 71 / 30%);
  text-underline-offset: 3px;
}

.contact-list {
  margin: 0;
  display: grid;
  gap: 20px;
}

.contact-list__row {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 18px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eae3d6;
}

.contact-list__row--last {
  border-bottom: none;
  padding-bottom: 0;
}

.contact-list__label {
  font-size: 16px;
  letter-spacing: 0.04em;
  color: #9a7b4f;
  padding-top: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.contact-list__label .icon {
  font-size: 18px;
}

.contact-list__value {
  margin: 0;
  font-size: 16px;
  color: #4a4438;
}

/* Additional information */
.info {
  margin: 76px 0 0;
  background: #fff;
  border-top: 1px solid #ebe3d4;
  border-bottom: 1px solid #ebe3d4;
}

.info__inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 56px 40px;
}

.info__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 48px;
}

.info-card:not(:first-child) {
  padding-left: 48px;
  border-left: 1px solid #e2dacb;
}

.info-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.info-card__icon {
  font-size: 30px;
}

.info-card__title {
  font-family: Spectral, serif;
  font-weight: 300;
  font-size: 22px;
  line-height: 1.25;
  margin: 0;
  color: #1d1a15;
}

.info-card__body {
  margin: 0 0 14px;
  font-size: 16px;
  color: #4a4438;
}

.info-card__link {
  font-size: 13.5px;
}

/* Closing */
.closing {
  position: relative;
  padding: 90px 40px;
  text-align: center;
  background-color: #2b2723;
  background-size: cover;
  background-position: center;
}

.closing__overlay {
  position: absolute;
  inset: 0;
  background: rgb(20 26 20 / 62%);
}

.closing__inner {
  position: relative;
  max-width: 820px;
  margin: 0 auto;
}

.closing__lead {
  font-family: Spectral, serif;
  font-size: 18px;
  line-height: 1.7;
  color: #f4f1ed;
  margin: 0;
}

.closing__rule {
  width: 44px;
  height: 1px;
  background: #c9a96a;
  margin: 34px auto;
}

.closing__note {
  font-size: 16px;
  color: #e5dfd4;
  margin: 0;
}

@media (max-width: 900px) {
  .hero__grid,
  .payment__grid,
  .cancel-contact,
  .info__grid {
    grid-template-columns: 1fr;
  }

  .step-card__body {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .step-card__col--guest {
    padding-left: 0;
    border-left: none;
  }

  .cancel-contact__col--cancel {
    padding-right: 0;
    border-right: none;
    padding-bottom: 48px;
    border-bottom: 1px solid #e2dacb;
  }

  .info-card:not(:first-child) {
    padding-left: 0;
    border-left: none;
  }

  .hero__title {
    font-size: 36px;
  }

  .container,
  .hero,
  .steps,
  .urgent,
  .payment__inner,
  .cancel-contact,
  .info__inner,
  .closing {
    padding-left: 16px;
    padding-right: 16px;
  }

  .band__imgwrap--full {
    display: none;
  }

  .band__imgwrap--half {
    display: block;
  }
}
</style>
